import { useDispatch, useSelector } from 'react-redux';
import { SelectionPermission } from 'store/productManagement/reducer.types';
import { clearSingleTarget, hideSecondaryPanel } from 'store/productManagement/reducer';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import SectionButton from 'lib/SectionButton';
import { SectionButtonShadow, SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { Store } from 'components/common/types/Store.types';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import { TARGET_KEY_TO_BORDER_RADIUS_MAPPING } from 'components/pages/Planner/PlannerSections/components/TargetsButton/TargetsButton.types';
import { TargetKey } from 'components/pages/Planner/PlannerSections/types';
import { getIsReadOnly } from 'store/productManagement/selectors';
import { groupTargets, hasTargets } from './utils';

export const ProductTargetsButton: React.FC = () => {
  const isReadOnly = useSelector(getIsReadOnly);
  const budget = useSelector((state: Store) => state.productManagement.newProduct?.budget);
  const frames = useSelector((state: Store) => state.productManagement.newProduct?.frames);
  const impressions = useSelector((state: Store) => state.productManagement.newProduct?.impressions);
  const networkSelectedPermission = useSelector(
    (state: Store) => state.productManagement.newProduct.networks?.selectionPermission,
  );

  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const isTargetsOpen = secondaryPanelType === PlannerSecondaryPanelType.TARGETS;

  const { localeCode, currencyCode } = useSelector((state: Store) => state.publisher.configuration);

  const dispatch = useDispatch();
  const onChangePanelVisibility = useOnChangePanelVisibility();

  const groupedTargets = groupTargets({ budget, frames, impressions }, localeCode, currencyCode);

  const isRequired =
    networkSelectedPermission !== SelectionPermission.FIXED_VALUE &&
    networkSelectedPermission !== SelectionPermission.RANGE_VALUE &&
    networkSelectedPermission !== SelectionPermission.ALL_OPTIONS &&
    !hasTargets({ budget, frames, impressions });

  const onClearTarget = (key: TargetKey): void => {
    dispatch(clearSingleTarget({ targetName: key }));
    dispatch(hideSecondaryPanel());
  };

  return (
    <div data-test-id="grouped-section-button" className="shadow-inner-sm rounded-md">
      {groupedTargets.map(({ label, value, key, icon }) => {
        return (
          <SectionButton
            key={key}
            dataTestId={`product-${key}-targets-button`}
            theme={SectionButtonTheme.SOLID}
            label={label}
            onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.TARGETS)}
            onClear={() => onClearTarget(key)}
            onRemoveChip={() => onClearTarget(key)}
            icon={icon}
            rounded={TARGET_KEY_TO_BORDER_RADIUS_MAPPING[key as keyof typeof TARGET_KEY_TO_BORDER_RADIUS_MAPPING]}
            shadow={SectionButtonShadow.NONE}
            isActive={isTargetsOpen}
            isRequired={isRequired}
            selected={value}
            isEllipsisDisabled={isReadOnly}
          />
        );
      })}
    </div>
  );
};
